import type { PreviewOptions, PrintElement, PrintJobSnapshot, PaginationStrategy } from "../core/types";
import { estimatePreviewPagination } from "./estimatePreviewPagination";
import { writeIframeDocument } from "./iframeAdapter";

function buildEstimatedSnapshot(snapshot: PrintJobSnapshot, pages: Array<{ elements: PrintElement[] }>): PrintJobSnapshot {
  const estimatedElements: PrintElement[] = [];

  pages.forEach((page, index) => {
    estimatedElements.push(...page.elements);

    if (index < pages.length - 1) {
      estimatedElements.push({ kind: "pageBreak" });
    }
  });

  return {
    ...snapshot,
    elements: estimatedElements
  };
}

// Detect template-style jobs by looking for page-positioned elements. These are
// safe to print, but preview must not feed them into flow-based estimation.
function hasPositionedTemplateElements(snapshot: PrintJobSnapshot): boolean {
  return snapshot.elements.some((element) => element.box?.x !== undefined || element.box?.y !== undefined);
}

function resolvePaginationStrategy(snapshot: PrintJobSnapshot, options: PreviewOptions): PaginationStrategy {
  const requested = options.paginationStrategy ?? "estimate";

  // Estimated pagination only makes sense for flowing content. Positioned
  // template elements should keep coordinate correctness and therefore fall back
  // to manual page handling in preview.
  if (requested === "estimate" && hasPositionedTemplateElements(snapshot)) {
    return "manual";
  }

  return requested;
}

// Render the current print job into a visible iframe. Paged preview can either
// respect only manual breaks or run a second measurement pass to estimate where
// the browser is likely to paginate long content.
export async function previewInIframe(
  snapshot: PrintJobSnapshot,
  options: PreviewOptions
): Promise<HTMLIFrameElement> {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("title", snapshot.title);
  iframe.style.width = "100%";
  iframe.style.minHeight = "1000px";
  iframe.style.border = "0";
  iframe.style.background = "#eef2f7";

  options.container.innerHTML = "";
  options.container.appendChild(iframe);

  const previewMode = options.mode ?? "document";
  const paginationStrategy = resolvePaginationStrategy(snapshot, options);

  if (previewMode === "paged" && paginationStrategy === "estimate") {
    // First write a continuous preview so the iframe can measure real DOM
    // heights using the current fonts, margins, images, and table layout.
    await writeIframeDocument(iframe, snapshot, "preview", {
      previewMode: "document",
      paginationStrategy: "manual",
      showPageGap: false,
      showPageNumbers: false,
      respectPageBreaks: options.respectPageBreaks,
      showAutoPaginationHints: false
    });

    const measureDocument = iframe.contentDocument;

    if (!measureDocument) {
      throw new Error("Unable to access iframe document for preview measurement.");
    }

    const estimatedPages = estimatePreviewPagination(measureDocument, snapshot);
    const estimatedSnapshot = buildEstimatedSnapshot(snapshot, estimatedPages);

    // Re-render the preview as explicit pages so the user can inspect estimated
    // page boundaries without changing the actual print() behavior.
    await writeIframeDocument(iframe, estimatedSnapshot, "preview", {
      previewMode: "paged",
      paginationStrategy: "manual",
      showPageGap: options.showPageGap,
      showPageNumbers: options.showPageNumbers,
      respectPageBreaks: true,
      showAutoPaginationHints: options.showAutoPaginationHints
    });
  } else {
    await writeIframeDocument(iframe, snapshot, "preview", {
      previewMode,
      paginationStrategy,
      showPageGap: options.showPageGap,
      showPageNumbers: options.showPageNumbers,
      respectPageBreaks: options.respectPageBreaks,
      showAutoPaginationHints: options.showAutoPaginationHints
    });
  }

  if (options.zoom && iframe.contentDocument?.body) {
    iframe.contentDocument.body.style.zoom = String(options.zoom);
  }

  return iframe;
}
